"use client"

import React from "react"
import { Slot } from "@radix-ui/react-slot"
import TC_Wrapper from "react-tag-commander"

import { buildAnalyticsActionLinks, cn, GetSlug } from "@/lib/utils"
import { Chip } from "@/components/ui/atoms"

export interface ChipFilterListProps {
  asChild?: boolean
  // Heterogeneous card props (ICardAnnuaireProps, ICardEventProps, ...); only
  // read dynamically via `filteringEl` to build the filter chips below.
  list: unknown[]
  selectedTagLabel?: string | null
  className?: string
  filteringEl?: string
  componentTitle?: string
  setSelectedTagLabel?: (arg0: string | null) => void
}

function getTagLabel(list: unknown[], filteringEl?: string): string[] {
  const key = filteringEl ? filteringEl : "tagLabel"
  return [...new Set(list.map((item) => (item as Record<string, string>)[key]))]
}

const ChipFilterList = React.forwardRef<HTMLDivElement, ChipFilterListProps>(
  (
    {
      asChild = false,
      list,
      selectedTagLabel,
      className,
      filteringEl,
      setSelectedTagLabel,
      componentTitle,
      ...props
    },
    ref
  ) => {
    const Component = asChild ? Slot : "div"
    const wrapper = TC_Wrapper.getInstance()

    const listTags = getTagLabel(list, filteringEl)
    const handleTagClick = (
      label: string,
      labelDisplay: string,
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      if (setSelectedTagLabel) {
        const analytics = buildAnalyticsActionLinks([
          componentTitle ?? "undefined",
          "Filtre",
          labelDisplay,
        ])
        wrapper.triggerEvent(
          analytics.type,
          event.currentTarget,
          analytics.chapters
        )

        wrapper.triggerEvent("action", event.currentTarget, {
          click: labelDisplay,
          click_chapter1: "Filter",
        })
        if (selectedTagLabel === label) {
          setSelectedTagLabel(null)
          updateURL("")
          return
        }

        setSelectedTagLabel(label)
        updateURL(label)
      }
    }

    const updateURL = (newParam: string) => {
      const searchParams = new URLSearchParams(window.location.search)
      searchParams.set("subtheme", newParam)
      if (searchParams) {
        const newURL = `${window.location.pathname}?${searchParams.toString()}`
        window.history.pushState({ path: newURL }, "", newURL)
      }
    }

    return (
      <Component
        className={cn(className, "chip-filter-list")}
        ref={ref}
        {...props}
      >
        {listTags.map((label) => {
          const slugifiedLabel = GetSlug(label)
          const selectedModifier =
            `tag-${slugifiedLabel}` === selectedTagLabel
              ? "bg-secondary-500 text-dark border-secondary-500"
              : ""

          return (
            <Chip
              key={slugifiedLabel}
              title={label}
              asChild={false}
              taglabel={`tag-${slugifiedLabel}`}
              className={cn("w-auto", selectedModifier)}
              onClick={(event) =>
                handleTagClick(`tag-${slugifiedLabel}`, label, event)
              }
            />
          )
        })}
      </Component>
    )
  }
)
ChipFilterList.displayName = "ChipFilterList"

export { ChipFilterList }
